/* eslint-disable no-restricted-globals */
import { Dispatch } from 'react'
import {
    clearUserCookies,
    authRefreshPath,
    retrieveUserInfoToken,
    getAuthStateFromUserInfoToken,
    IdTokenDecodedJWT,
} from '@news-mono/web-common'
import {
    authLogin,
    authLogout,
    authEntitlementsChecked,
    authRefreshLogin,
    authSessionExpired,
    authNetworkError,
    AuthenticationState,
} from '@news-mono/web-common'
import { UserAuthEvents } from '@news-mono/web-common'
import { Logger } from 'typescript-log'
import {
    BeginLoadingEvent,
    EndLoadingEvent,
} from 'react-ssr-data-loader/dist/cjs/events'

export function checkUserAuth(
    dispatch: Dispatch<any>,
    onEvent: (authEvent: UserAuthEvents) => void,
) {
    const userState = retrieveUserInfoToken()

    if (userState) {
        const authState = getAuthStateFromUserInfoToken(userState, onEvent)

        if (authState) {
            dispatch(authLogin(authState))
        }
    }
    dispatch(authEntitlementsChecked())
}

export async function checkIdTokenAndRefresh(params: {
    idToken: IdTokenDecodedJWT | null
    dispatch: Dispatch<any>
    authState: AuthenticationState
    logger: Logger
    onEvent: (
        authEvent: UserAuthEvents | BeginLoadingEvent | EndLoadingEvent,
    ) => void
    options: {
        invokation: 'manual' | 'auto'
        autoRefreshToken: boolean
    }
}) {
    const { idToken, dispatch, authState, logger, onEvent, options } = params
    const { invokation, autoRefreshToken } = options

    try {
        if (invokation === 'auto') {
            onEvent({
                type: 'begin-loading-event',
                data: {
                    resourceType: 'auth-check-refresh-token',
                    resourceLoadParamsHash: '',
                },
            })
        }
        const { isLoggedIn } = authState

        if (!idToken) {
            logger.debug(
                { invokation },
                'No ID token cookie found, logging out',
            )
            isLoggedIn && dispatch(authLogout({ onEvent }))
            return
        }

        const userInfo = retrieveUserInfoToken()

        const forcedExpiry =
            typeof location !== 'undefined' &&
            location.search.indexOf('force_access_token_expiry') !== -1
        const isTokenExpired =
            idToken.exp < new Date().getTime() / 1000 || forcedExpiry

        dispatch(authEntitlementsChecked())

        if (!isTokenExpired) {
            logger.debug({ invokation }, 'ID Token not expired')
            if (userInfo) {
                const authState = getAuthStateFromUserInfoToken(
                    userInfo,
                    onEvent,
                )
                dispatch(authRefreshLogin(authState))
            }
            return
        }

        if (!autoRefreshToken) {
            return
        }

        try {
            const userInfo = retrieveUserInfoToken()
            logger.debug(
                { userInfo, idToken, invokation },
                'idToken has expired, refreshing... authTokens / userToken',
            )

            const authResult = await fetch(authRefreshPath, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                // Required to set cookies
                credentials: 'same-origin',
            })

            if (authResult.status !== 200) {
                // When the authentication server responds with any other status other than 200 error
                // we've now entered a state we can't recover from. The users session will be expired
                // and the stale cookies cleared.
                dispatch(authSessionExpired({ onEvent }))
                clearUserCookies()
                return
            }

            if (userInfo) {
                const authState = getAuthStateFromUserInfoToken(
                    userInfo,
                    onEvent,
                )
                dispatch(authRefreshLogin(authState))
                logger.debug(
                    { userInfo, idToken, invokation },
                    'userToken / authTokens refreshed successfully',
                )
            }
        } catch (error) {
            // Recovery should be possible when a network error occurs.
            // Flag a network error and on the next refresh attempt try again.
            dispatch(authNetworkError({ onEvent }))
            return
        }
    } finally {
        if (invokation === 'auto') {
            onEvent({
                type: 'end-loading-event',
                data: {
                    resourceType: 'auth-check-refresh-token',
                    resourceLoadParamsHash: '',
                },
            })
        }
    }
}
